import {Component, inject, OnInit, ViewChild} from '@angular/core';

import {GlobalModelService} from "../../../services/GlobalModel.service";
import {Partition} from "../../../models/partition/Partition";
import {PartitionOpeService} from "../../../services/ope/partitionOpe.service";
import {PutPartitionCB} from "../../../ope/partition/PutPartitionCB";
import {DxFormComponent} from "devextreme-angular";
import {DelPartitionCB} from "../../../ope/partition/DelPartitionCB";
import {UpdateParentPartitionCB} from "../../../ope/partition/UpdateParentPartitionCB";
import {BaseComponentWithDrawer} from "../base-component-with-drawer.component";
import {GetPartitionHierarchyCB} from "../../../ope/partition/GetPartitionHierarchyCB";
import {GetPartitionDetailCB} from "../../../ope/partition/GetPartitionDetailCB";

@Component({
  selector: 'app-partition',
  templateUrl: './partition.component.html',
  styleUrls: ['./partition.component.css', '../../common/drawer-style.css', '../base-component-with-drawer.component.css']
})
export class PartitionComponent extends BaseComponentWithDrawer implements OnInit {

  gmService: GlobalModelService = inject(GlobalModelService);
  partitionService: PartitionOpeService = inject(PartitionOpeService);

  @ViewChild('newPartitionForm', {static: false}) newPartitionForm!: DxFormComponent;
  @ViewChild('editPartitionForm', {static: false}) editPartitionForm!: DxFormComponent;

  /**
   * All partitions
   */
  partitions: Partition[] = [];

  /**
   * Partition tab for adding/editing a partition
   */
  partitionTabs: any[] = [
    {
      id: 0,
      text: "Informazioni"
    },
    {
      id: 1,
      text: "Dettagli"
    },
    {
      id: 2,
      text: "Indirizzo"
    }
  ];

  /**
   * Selected partition on tree list row click
   */
  selectedPartition?: Partition;

  /**
   * Copy of selected partition, storing temporary changes (can roll back to selectedPartition if something goes wrong)
   */
  editedPartition?: Partition;

  /**
   * New partition used when opening new partition form
   */
  newPartition?: Partition = new Partition();

  /**
   * Enable lateral drawer form when true
   */
  isEditMode: boolean = false;

  /**
   * Tab selected partition form (new/edit)
   */
  selectedPartitionTab: number = 0;

  /**
   * Open new partition dialog when true
   */
  addPartition: boolean = false;

  /**
   * Open delete partition dialog when true
   */
  deletingPartition: boolean = false;

  /**
   * True if the user changed anything in edit partition form
   */
  partitionEdited: boolean = false;

  /**
   * show change parent partition form when true
   */
  editParent: boolean = false;

  /**
   * True if new partition is valid and can be saved
   */
  isNewFormValid: boolean | undefined = false;

  /**
   * True if edited partition is valid and can be updated
   */
  isEditFormValid: boolean | undefined = false;

  /**
   * Id of the selected partition in the main tree list (selected by clicking on a row)
   */
  selectedRowPartition: any;

  /**
   * Id of the selected partition in the new partition form tree list (selected by clicking on a row)
   */
  selectedRowNewPartitionForm: any;

  /**
   * Id of the selected partition in the change parent partition form tree list (selected by clicking on a row)
   */
  selectedRowEditParentPartitionForm: any;

  /**
   * Filtered list of partitions without the selected partition
   */
  selectableParentPartitions: Partition[] = [];


  /**
   * Text box options for partition fields
   */
  partitionNewTextOptions = {
    valueChangeEvent: 'keyup',
  };

  partitionNewTextAreaOptions = {
    valueChangeEvent: 'keyup',
    autoResizeEnabled: true,
    maxLength: this.gmService.STR_DIM_MAX
  };

  partitionEditTextOptions = {
    valueChangeEvent: 'keyup',
    disabled: !this.isEditMode
  };

  partitionEditTextAreaOptions = {
    valueChangeEvent: 'keyup',
    disabled: !this.isEditMode,
    autoResizeEnabled: true
  };

  partitionEditCheckBoxOptions = {
    disabled: !this.isEditMode
  };

  partitionEditSwitchOptions = {
    disabled: !this.isEditMode
  };


  ngOnInit() {
    super.ngOnInit();
    this.getPartitions();
  }

  /**
   * Get all partitions
   */
  getPartitions() {
    const cb = new GetPartitionHierarchyCB();

    this.partitionService.getPartitionHierarchy(cb).then((_) => {

      this.partitions = cb.o.partitions;

      // if the drawer was already open, update the infos (probably a partition has been updated)
      if (!!this.selectedPartition) {
        this.selectedPartition = this.partitions.find(c => c.id === this.selectedPartition?.id);
        if (!!this.selectedPartition) {
          this.editedPartition = JSON.parse(JSON.stringify(this.selectedPartition));
        }
      }

    }).catch((error) => {
      if (!!this.selectedPartition)
        this.editedPartition = JSON.parse(JSON.stringify(this.selectedPartition));
    });
  }

  /**
   * Add or update partition
   */
  putPartition(partition: Partition | undefined) {

    // create cb
    const cb = new PutPartitionCB();

    cb.i = partition!;

    this.partitionService.putPartition(cb).then((_) => {
      this.addPartition = false;

      this.isNewFormValid = false;

      if (!partition!.id)
        this.toastService.openSuccessToastMessage(this.translate.instant("REGISTRY.PARTITION.TEXT.CREATED"));
      else
        this.toastService.openSuccessToastMessage(this.translate.instant("REGISTRY.PARTITION.TEXT.UPDATED"));

      // reload partitions list
      this.getPartitions();
    }).catch((error) => {

      // roll back to previous partition version
      if (!!this.selectedPartition)
        this.editedPartition = JSON.parse(JSON.stringify(this.selectedPartition));
    });
  }

  /**
   * Open drawer when clicking on a tree list row (only if the event is not a click on the arrow to open the children)
   * @param $event
   */
  onPartitionRowClick($event: any) {
    if (!($event.event.target.parentElement.classList.contains("dx-treelist-expanded") || $event.event.target.parentElement.classList.contains("dx-treelist-collapsed"))) {
      if (!this.selectedPartition) {
        const cb = new GetPartitionDetailCB();
        cb.i.partitionId = $event.data.id;
        this.isDrawerOpen = true;
        this.partitionService.getPartitionDetail(cb).then(
          () => {
            this.selectedPartition = cb.o.partition;
            this.editedPartition = JSON.parse(JSON.stringify(this.selectedPartition));
          }
        )
      } else {
        if (this.selectedPartition!.id === $event.data.id) {
          this.closeDrawer();
        } else {
          this.switchPartition($event.data);
        }
      }
    }
  }

  switchPartition(partition: Partition) {
    if (this.partitionEdited && this.isEditMode) {
      this.showDiscardEditsDialog().show().done((dialogResult: any) => {
        if (dialogResult) {
          this.resetDrawer();
          const cb = new GetPartitionDetailCB();
          cb.i.partitionId = partition.id;
          this.isDrawerOpen = true;
          this.partitionService.getPartitionDetail(cb).then(
            () => {
              this.selectedPartition = cb.o.partition;
              this.editedPartition = JSON.parse(JSON.stringify(this.selectedPartition));
            }
          )
        } else {
          this.selectedRowPartition = [this.selectedPartition!.id];
        }
      });
    } else {
      this.resetDrawer();
      const cb = new GetPartitionDetailCB();
      cb.i.partitionId = partition.id;
      this.isDrawerOpen = true;
      this.partitionService.getPartitionDetail(cb).then(
        () => {
          this.selectedPartition = cb.o.partition;
          this.editedPartition = JSON.parse(JSON.stringify(this.selectedPartition));
        }
      )
    }

  }

  /**
   * Call putPartition ope with new partition
   */
  createNewPartition() {
    this.checkNewPartitionValidation();

    if (this.isNewFormValid)
      this.putPartition(this.newPartition);
  }

  /**
   * Call putPartition ope with edited partition
   */
  savePartition() {
    this.isEditMode = false;
    this.updatePartitionTextOptions();

    this.putPartition(this.editedPartition);
  }

  /**
   * Reset new partition object
   */
  resetNewPartition() {
    this.newPartition = new Partition();
  }

  /**
   * Roll back edited partition
   */
  resetEditedPartition() {
    this.editedPartition = JSON.parse(JSON.stringify(this.selectedPartition));
  }

  /**
   * Check if new partition is valid, to enable SAVE button
   */
  checkNewPartitionValidation() {
    const result = this.newPartitionForm.instance.validate();

    this.isNewFormValid = result.isValid;
  }

  /**
   * Check if edited partition is valid, to enable SAVE button
   */
  checkEditPartitionValidation() {
    const result = this.editPartitionForm.instance.validate();

    this.isEditFormValid = result.isValid;

    this.partitionEdited = JSON.stringify(this.editedPartition) !== JSON.stringify(this.selectedPartition);

    this.appService.setRegistryEntityIsChanged(this.partitionEdited && this.isEditMode);
  }

  /**
   * Enable form edit in drawer
   */
  enableEditMode() {
    this.isEditMode = true;
    this.updatePartitionTextOptions();
  }

  /**
   * Disable form edit in drawer
   */
  disableEditMode() {
    if (this.partitionEdited && this.isEditMode) {
      this.showDiscardEditsDialog().show().done((dialogResult: any) => {
        if (dialogResult) {
          this.resetDrawer();
        }
      });
    } else {
      this.resetDrawer();
    }
  }

  /**
   * Close lateral drawer
   */
  closeDrawer() {
    if (this.partitionEdited && this.isEditMode) {
      this.showDiscardEditsDialog().show().done((dialogResult: any) => {
        if (dialogResult) {
          this.resetDrawer();
          this.isDrawerOpen = false;
          this.selectedPartitionTab = 0;
          this.resetPartitionGrid();
        }
      });
    } else {
      this.resetDrawer();
      this.isDrawerOpen = false;
      this.selectedPartitionTab = 0;
      this.resetPartitionGrid();
    }
  }

  /**
   * Reset all drawer vars
   */
  resetDrawer() {
    if (!!this.selectedPartition) {
      this.editedPartition = JSON.parse(JSON.stringify(this.selectedPartition));
    }
    this.partitionEdited = false;
    this.isEditMode = false;
    this.updatePartitionTextOptions();
  }

  resetPartitionGrid() {
    this.selectedPartition = undefined;
    this.editedPartition = undefined;
    this.selectedRowPartition = [];
  }

  /**
   * Show the edit parent dialog
   */
  showEditParentForm() {
    this.selectedRowEditParentPartitionForm = [this.selectedPartition!.parentId];
    this.selectableParentPartitions = this.partitions.filter(p => p.id !== this.selectedPartition?.id);
    this.editParent = true;
  }

  /**
   * Show the new partition dialog
   */
  showNewPartitionForm() {
    this.newPartition = new Partition();
    this.addPartition = true;
  }

  /**
   * Update form fields when enabling/disabling edit mode
   */
  updatePartitionTextOptions() {

    this.partitionNewTextOptions = {
      valueChangeEvent: 'keyup',
    };

    this.partitionNewTextAreaOptions = {
      valueChangeEvent: 'keyup',
      autoResizeEnabled: true,
      maxLength: this.gmService.STR_DIM_MAX
    };

    this.partitionEditTextOptions = {
      valueChangeEvent: 'keyup',
      disabled: !this.isEditMode
    };

    this.partitionEditTextAreaOptions = {
      valueChangeEvent: 'keyup',
      disabled: !this.isEditMode,
      autoResizeEnabled: true
    };

    this.partitionEditCheckBoxOptions = {
      disabled: !this.isEditMode
    };

    this.partitionEditSwitchOptions = {
      disabled: !this.isEditMode
    };

  }

  /**
   * Ope to delete a partition
   */
  deletePartition() {

    // close delete partition dialog
    this.deletingPartition = false;

    // create cb
    const cb: DelPartitionCB = new DelPartitionCB();
    cb.i.id = this.selectedPartition!.id;

    // call ope
    this.partitionService.delPartition(cb).then((_) => {
      this.toastService.openSuccessToastMessage(this.translate.instant("REGISTRY.PARTITION.TEXT.DELETED"));
      this.getPartitions();
      this.isDrawerOpen = false;
      this.selectedPartition = undefined;
    });
  }

  onSelectNewParentClick($event: any) {
    if (!($event.event.target.parentElement.classList.contains("dx-treelist-expanded") || $event.event.target.parentElement.classList.contains("dx-treelist-collapsed"))) {
      this.newPartition!.parentId = $event.data.id;
    }
  }

  onSelectEditParentClick($event: any) {
    if (!($event.event.target.parentElement.classList.contains("dx-treelist-expanded") || $event.event.target.parentElement.classList.contains("dx-treelist-collapsed"))) {
      // if($event.data.partitionId != this.editedPartition!.partitionId)
      this.editedPartition!.parentId = $event.data.id;
    }
  }

  closeEditParentForm() {
    this.editParent = false;
    this.editedPartition = JSON.parse(JSON.stringify(this.selectedPartition));
  }

  updateParentPartition() {

    const cb: UpdateParentPartitionCB = new UpdateParentPartitionCB();

    cb.i.id = this.editedPartition!.id;
    cb.i.parentId = this.editedPartition!.parentId!;

    this.partitionService.updateParentPartition(cb).then((_) => {
      this.editParent = false;
      this.isDrawerOpen = false;
      this.selectedPartition = undefined;
      this.editedPartition = undefined;
      this.toastService.openSuccessToastMessage(this.translate.instant("REGISTRY.PARTITION.TEXT.UPDATED"));
      this.getPartitions();
    });
  }

  checkIfParentChanged() {
    return this.editedPartition?.parentId !== this.selectedPartition?.parentId
      && this.editedPartition?.parentId !== this.editedPartition?.id;
  }
}
